import React, { useEffect, useRef, useState } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast, ToastMessage } from "primereact/toast";
import UserService from "../../services/user/user.service";
import AppLoading from "../app-loading/app-loading";

interface Role {
  roleID: number;
  name: string;
  description: string | null;
}

const RoleManagement: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "" });
  const [creatingRole, setCreatingRole] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await UserService.GetAllRoles();
      if (response) setRoles(response);
    } catch {
      showMessage("Error", "Failed to fetch roles", "error");
    } finally {
      setLoading(false);
    }
  };

const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const nameEditor = (options: ColumnEditorOptions) => (
    <InputText
      value={options.value}
      onChange={(e) => options.editorCallback!(e.target.value)}
      style={{ width: "100%", border: "1px solid green" }}
    />
  );

  const descriptionEditor = (options: ColumnEditorOptions) => (
    <InputText
      value={options.value || ''}
      onChange={(e) => options.editorCallback!(e.target.value)}
      style={{ width: "100%", border: "1px solid green" }}
    />
  );

  const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
  const { newData } = e;

  if (!newData.name?.trim()) {
    showMessage("Warning", "Role name is required", "warn");
    return;
  }

  try {
    const result = await UserService.UpdateRole({
      roleID: newData.roleID,
      name: newData.name,
      description: newData.description || ""
    });

    if (result.isSuccess) {
      showMessage("Success", result.message || "Role updated successfully", "success");
      setTimeout(() => {
        fetchRoles();
      }, 1000);
    } else {
      showMessage("Error", result.message || "Failed to update role", "error");
    }
  } catch (error) {
    showMessage("Error", "An unexpected error occurred while updating role", "error");
  }
};

  const actionBodyTemplate = (rowData: Role) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text"
      style={{ color: "gray" }}
      onClick={() => confirmDelete(rowData)}
    />
  );

  const confirmDelete = (rowData: Role) => {
    setSelectedRole(rowData);
    setShowDeleteDialog(true);
  };

  const onHideDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedRole(null);
  };

 const deleteRole = async () => {
  if (!selectedRole) return;

  setDeleteLoading(true);
  try {
    const result = await UserService.DeleteRole(selectedRole.roleID);
    
    if (result.isSuccess) {
      showMessage("Success", result.message || `Role "${selectedRole.name}" deleted successfully`, "success");
      setTimeout(() => {
        fetchRoles();
      }, 1000);
    } else {
      showMessage("Error", result.message || "Failed to delete role", "error");
    }
  } catch (error: any) {
    showMessage("Error", error.message || "An error occurred while deleting the role", "error");
  } finally {
    setDeleteLoading(false);
    onHideDeleteDialog();
  }
};

  const openAddDialog = () => {
    setNewRole({ name: "", description: "" });
    setShowAddDialog(true);
  };

  const createRole = async () => {
  if (!newRole.name.trim()) {
    showMessage("Warning", "Role name is required", "warn");
    return;
  }

  setCreatingRole(true);
  try {
    const result = await UserService.CreateRole(newRole);
    
    if (result.isSuccess) {
      showMessage("Success", `Role "${newRole.name}" created successfully`, "success");
      setTimeout(() => {
        fetchRoles();
      }, 1000);
      setShowAddDialog(false);
    } else {
      showMessage("Error", result.message || "Failed to create role", "error");
    }
  } catch (error) {
    showMessage("Error", "An error occurred while creating role", "error");
  } finally {
    setCreatingRole(false);
  }
};

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center min-h-screen">
        <AppLoading/>
      </div>
    );
  }

  return (
  <>
   <Toast ref={toast} position="top-center" />
    <div className="col-12">
      <div className="card border-noround p-3">
        <h5 className="ml-1 mb-0">Role Management</h5>
        <div className="flex justify-content-end mb-3">
          <Button
            label="Create New Role"
            icon="pi pi-plus"
            className="p-button-success mb-2"
            onClick={openAddDialog}
          />
        </div>
        <DataTable
          value={roles}
          editMode="row"
          dataKey="roleID"
          onRowEditComplete={onRowEditComplete}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          emptyMessage="No roles found"
        >
          <Column field="name" header="Name" editor={nameEditor} />
          <Column field="description" header="Description" editor={descriptionEditor} />
          <Column rowEditor header="Edit" headerStyle={{ width: "10%" }} bodyStyle={{ textAlign: "center" }} />
          <Column body={actionBodyTemplate} header="Actions" headerStyle={{ width: "10%" }} bodyStyle={{ textAlign: "center" }} />
        </DataTable>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteDialog}
        header="Confirm Delete"
        modal
        onHide={onHideDeleteDialog}
        footer={
          <div>
            <Button label="Yes" icon="pi pi-check" className="p-button-danger" onClick={deleteRole} loading={deleteLoading} />
          </div>
        }
      >
        <div className="confirmation-content">
          {selectedRole && (
            <span>
              Are you sure you want to delete <strong>{selectedRole.name}</strong>?
            </span>
          )}
        </div>
      </Dialog>

      {/* Add New Role Dialog */}
      <Dialog
        visible={showAddDialog}
        header="Create New Role"
        modal
        style={{ width: '30rem' }}
        onHide={() => setShowAddDialog(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Save" icon="pi pi-check" className="p-button-success" onClick={createRole} loading={creatingRole} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label htmlFor="name">Name</label>
            <InputText
              id="name"
              value={newRole.name}
              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
               style={{ width: '100%' }}  // Explicit width
              className="p-inputtext"  
              placeholder="Name"
              required
            />
          </div>
          <div className="field col-12">
            <label htmlFor="description">Description</label>
            <InputText
              id="description"
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
               style={{ width: '100%' }}  // Explicit width
               className="p-inputtext" 
               placeholder="Description"
            />
          </div>
        </div>
      </Dialog>
    </div>
    </>
  );
};

export default RoleManagement;
